import { useRef, useState } from "react"

import { useDebouncedCallback } from "../../../../hooks/use-debounced-callback.ts"
import { searchCustomers } from "../../../CustomersPage/api/services.ts"
import type { CustomerSearchResult } from "../../../CustomersPage/types/customer.types.ts"

const SEARCH_DEBOUNCE_MS = 500
const SEARCH_MIN_QUERY_LENGTH = 2
const SEARCH_PAGE_SIZE = 20

function resultsOf(data: unknown): CustomerSearchResult[] {
  if (Array.isArray(data)) return data as CustomerSearchResult[]
  const results = (data as { results?: CustomerSearchResult[] })?.results
  return Array.isArray(results) ? results : []
}

function useCustomerSearch() {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<CustomerSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const latest = useRef(0)

  const discard = () => {
    latest.current += 1
    setResults([])
    setLoading(false)
  }

  const run = useDebouncedCallback((search: string) => {
    void (async () => {
      const seq = ++latest.current
      setLoading(true)

      try {
        const response = await searchCustomers({
          q: search,
          page: 1,
          page_size: SEARCH_PAGE_SIZE
        })
        if (seq !== latest.current) return
        setResults(response.ok ? resultsOf(response.data) : [])
      } catch {
        if (seq !== latest.current) return
        setResults([])
      } finally {
        if (seq === latest.current) setLoading(false)
      }
    })()
  }, SEARCH_DEBOUNCE_MS)

  const search = (value: string) => {
    setQuery(value)

    if (value.trim().length < SEARCH_MIN_QUERY_LENGTH) {
      discard()
      return
    }

    run(value.trim())
  }

  const clear = () => {
    setQuery("")
    discard()
  }

  return { query, results, loading, search, clear }
}

export { SEARCH_DEBOUNCE_MS, SEARCH_MIN_QUERY_LENGTH, useCustomerSearch }
